/**
 * Consultas del asistente sobre los datos reales de HabitOS. Solo leen: reutilizan el motor
 * (evaluate, successRate, streaks) y los selectores de la app; nunca inventan cifras.
 *
 * Cada respuesta dice qué período y qué datos usó, y avisa cuando no hay datos suficientes.
 * Todo se calcula en el dispositivo: nada se envía a servicios externos.
 */
import { addDays, diffDays, weekPeriods, type LocalDate, type Schedule } from '@habitos/engine';
import type { Ctx } from '../domain/data';
import { buildAgenda, SLOTS } from '../domain/agenda';
import { focusStats } from '../domain/focusStats';
import { habitRates } from '../domain/progress';
import { currentVersion, toEngineSettings, type HabitRec } from '../domain/model';
import { searchNotes } from '../domain/notesSearch';
import { habitStats, indexData, liveHabits, todayFor, type HabitIndex } from '../domain/selectors';
import type { AppData } from '../domain/data';
import { makeT, type TFn } from '../i18n/core';
import { dayNames, formatDay } from './normalize';
import { tr, type Choice, type Part, type TextRef } from './parts';

export interface QueryEnv {
  data: AppData;
  ctx: Ctx;
  locale: 'es' | 'en';
}

export interface Refs {
  habitId: string | null;
  taskId: string | null;
  date: string | null;
}

export interface Answer {
  parts: Part[];
  choices?: Choice[];
  refs?: Partial<Refs>;
}

/** Por debajo de este porcentaje de cumplimiento, un hábito se considera descuidado. */
export const NEGLECT_THRESHOLD = 0.6;
/** Mínimo de periodos evaluables para opinar sobre un hábito. */
export const MIN_EVALUABLE = 3;

const text = (key: string, params?: Record<string, string | number>): Part => ({ t: 'text', text: tr(key, params) });
const caption = (key: string, params?: Record<string, string | number>): Part => ({ t: 'caption', text: tr(key, params) });
const say = (id: string, label: TextRef, sentence: string): Choice => ({ id, label, action: { type: 'say', text: sentence } });

export const activeHabits = (data: AppData): HabitRec[] => liveHabits(data.habits).filter((h) => h.kind !== 'task' && !h.archivedAt && !h.completedAt);
export const openTasks = (data: AppData): HabitRec[] => liveHabits(data.habits).filter((h) => h.kind === 'task' && !h.archivedAt && !h.completedAt);

export function describeSchedule(s: Schedule, t: TFn, locale: 'es' | 'en'): string {
  switch (s.type) {
    case 'daily':
      return t('assistant.freq.daily');
    case 'weekdays': {
      const days = s.days.length === 7 ? t('assistant.freq.daily') : dayNames(s.days, locale);
      return s.everyWeeks && s.everyWeeks > 1 ? t('assistant.freq.everyWeeks', { days, count: s.everyWeeks }) : days;
    }
    case 'yearly':
      return t('assistant.freq.yearly');
    case 'weekly_quota':
      return t('assistant.freq.quota', { count: s.times ?? 1 });
    default:
      return t('chat.freq.other');
  }
}

const pct = (r: number) => Math.round(r * 100);

interface Row {
  h: HabitRec;
  done: number;
  total: number;
  rate: number | null;
  last: LocalDate | null;
}

/** Cumplimiento por hábito en los periodos ya cerrados de [from, to] (misma regla que Hoy y Progreso). */
function rows(env: QueryEnv, idx: HabitIndex, from: LocalDate, to: LocalDate): Row[] {
  const { data, ctx } = env;
  const active = new Set(activeHabits(data).map((h) => h.id));
  return habitRates(data.habits, idx, data.vacations, toEngineSettings(data.settings!), ctx.today, from, to)
    .filter((r) => active.has(r.habit.id))
    .map((r) => {
      const last = ((idx.entriesBy.get(r.habit.id) ?? []).map((e) => e.logicalDate).sort().pop() ?? null) as LocalDate | null;
      return { h: r.habit, done: r.done, total: r.total, rate: r.rate, last };
    });
}

function rateItem(r: Row, t: TFn, locale: 'es' | 'en'): TextRef {
  const last = r.last ? t('chat.a.lastOn', { date: formatDay(r.last, locale, { day: 'numeric', month: 'short' }) }) : t('chat.a.neverLogged');
  return tr('chat.a.habitRate', { name: r.h.name, done: r.done, total: r.total, pct: pct(r.rate ?? 0), last });
}

/** "¿Qué hábitos estoy descuidando?": últimos 14 días cerrados (hoy no cuenta: sigue abierto). */
export function neglectedHabits(env: QueryEnv): Answer {
  const { data, ctx, locale } = env;
  const t = makeT(locale);
  const idx = indexData(data);
  const to = addDays(ctx.today, -1);
  const from = addDays(ctx.today, -14);
  const all = rows(env, idx, from, to);
  if (!all.length) return { parts: [text('chat.a.noHabits')], choices: [say('c-new', tr('chat.c.createHabit'), t('chat.examples.goal'))] };
  const enough = all.filter((r) => r.total >= MIN_EVALUABLE);
  const few = all.filter((r) => r.total < MIN_EVALUABLE);
  const neglected = enough.filter((r) => (r.rate ?? 0) < NEGLECT_THRESHOLD).sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0) || (a.last ?? '').localeCompare(b.last ?? ''));
  const parts: Part[] = [];
  if (neglected.length) {
    parts.push(text('chat.a.neglectedIntro', { count: neglected.length, threshold: pct(NEGLECT_THRESHOLD) }));
    parts.push({ t: 'list', title: null, items: neglected.slice(0, 8).map((r) => rateItem(r, t, locale)) });
  } else if (enough.length) {
    parts.push(text('chat.a.neglectedNone', { threshold: pct(NEGLECT_THRESHOLD) }));
    const lowest = [...enough].sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0)).slice(0, 3);
    parts.push({ t: 'list', title: tr('chat.a.lowest'), items: lowest.map((r) => rateItem(r, t, locale)) });
  } else {
    parts.push(text('chat.a.notEnoughData'));
  }
  if (few.length) parts.push(caption('chat.a.fewData', { names: few.map((r) => `«${r.h.name}»`).join(', '), min: MIN_EVALUABLE }));
  parts.push(caption('chat.a.periodStats', { from: formatDay(from, locale, { day: 'numeric', month: 'short' }), to: formatDay(to, locale, { day: 'numeric', month: 'short' }), days: 14 }));
  parts.push(caption('chat.a.sourceStats'));
  const top = neglected[0];
  return {
    parts,
    refs: top ? { habitId: top.h.id } : {},
    choices: top
      ? [
          say('c-remind', tr('chat.c.remind', { name: top.h.name }), t('chat.say.remind', { name: top.h.name })),
          say('c-progress', tr('chat.c.progress', { name: top.h.name }), t('chat.say.progress', { name: top.h.name })),
        ]
      : [],
  };
}

/** Resumen general de los últimos 7 días cerrados. */
export function statsSummary(env: QueryEnv): Answer {
  const { data, ctx, locale } = env;
  const t = makeT(locale);
  const to = addDays(ctx.today, -1);
  const from = addDays(ctx.today, -7);
  const all = rows(env, indexData(data), from, to);
  if (!all.length) return { parts: [text('chat.a.noHabits')] };
  const done = all.reduce((n, r) => n + r.done, 0);
  const total = all.reduce((n, r) => n + r.total, 0);
  const parts: Part[] = [];
  if (!total) parts.push(text('chat.a.notEnoughData'));
  else {
    parts.push(text('chat.a.summary', { done, total, pct: pct(done / total) }));
    const sorted = all.filter((r) => r.total > 0).sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
    parts.push({ t: 'list', title: null, items: sorted.slice(0, 8).map((r) => rateItem(r, t, locale)) });
  }
  parts.push(caption('chat.a.periodStats', { from: formatDay(from, locale, { day: 'numeric', month: 'short' }), to: formatDay(to, locale, { day: 'numeric', month: 'short' }), days: 7 }));
  parts.push(caption('chat.a.sourceStats'));
  return { parts, choices: [say('c-neglected', tr('chat.c.neglected'), t('chat.say.neglected'))] };
}

/** Progreso de un hábito concreto: racha, últimos 30 días y total. */
export function habitProgress(env: QueryEnv, h: HabitRec): Answer {
  const { data, ctx, locale } = env;
  const t = makeT(locale);
  const s = habitStats(h, data, indexData(data), ctx.today);
  const unit = t(`chat.unit.${s.streak.unit}`, { count: s.streak.current });
  const parts: Part[] = [text('chat.a.progressTitle', { name: h.name })];
  const items: TextRef[] = [tr('chat.a.streak', { current: s.streak.current, best: s.streak.best, unit })];
  const evaluable = s.rate30.completed + s.rate30.missed + s.rate30.failed;
  items.push(s.rate30.rate === null || evaluable === 0 ? tr('chat.a.rate30None') : tr('chat.a.rate30', { done: s.rate30.completed, total: evaluable, pct: pct(s.rate30.rate) }));
  items.push(tr('chat.a.completions', { count: s.completions }));
  parts.push({ t: 'list', title: null, items });
  if (evaluable < MIN_EVALUABLE) parts.push(caption('chat.a.fewDataOne', { min: MIN_EVALUABLE }));
  parts.push(caption('chat.a.periodProgress', { from: formatDay(h.startDate, locale, { day: 'numeric', month: 'short', year: 'numeric' }) }));
  return { parts, refs: { habitId: h.id }, choices: [say('c-remind', tr('chat.c.remind', { name: h.name }), t('chat.say.remind', { name: h.name }))] };
}

export function habitsOverview(env: QueryEnv): Answer {
  const { data, ctx, locale } = env;
  const t = makeT(locale);
  const habits = activeHabits(data);
  if (!habits.length) return { parts: [text('chat.a.noHabits')], choices: [say('c-new', tr('chat.c.createHabit'), t('chat.examples.goal'))] };
  const idx = indexData(data);
  const settings = toEngineSettings(data.settings!);
  const items = habits.slice(0, 30).map((h) => {
    const status = todayFor(h, idx, data.vacations, settings, ctx.today)?.ue?.status ?? 'not_scheduled';
    const state = t(`chat.status.${status === 'out_of_range' ? 'not_scheduled' : status}`);
    return tr('chat.a.habitLine', { name: h.name, freq: describeSchedule(currentVersion(h).schedule as Schedule, t, locale), today: state });
  });
  return {
    parts: [
      text('chat.a.habitsIntro', { count: habits.length }),
      { t: 'habits', ids: habits.slice(0, 12).map((h) => h.id) },
      { t: 'list', title: null, items, plain: habits.length <= 12 },
      caption('chat.a.sourceLocal'),
    ],
    refs: habits.length === 1 ? { habitId: habits[0]!.id } : {},
    choices: [say('c-neglected', tr('chat.c.neglected'), t('chat.say.neglected'))],
  };
}

export function tasksOverview(env: QueryEnv): Answer {
  const { data, ctx, locale } = env;
  const t = makeT(locale);
  const tasks = openTasks(data);
  if (!tasks.length) return { parts: [text('chat.a.noTasks')], choices: [say('c-task', tr('chat.c.createTask'), t('chat.examples.task'))] };
  const when = (h: HabitRec): string => {
    if (!h.dueDate) return t('chat.a.undated');
    const d = formatDay(h.dueDate, locale, { weekday: 'short', day: 'numeric', month: 'short' });
    const time = h.dueTime ? ` · ${h.dueTime}` : '';
    if (h.dueDate < ctx.today) return t('chat.a.overdue', { date: d });
    if (h.dueDate === ctx.today) return `${t('chat.a.today')}${time}`;
    return `${d}${time}`;
  };
  const order = (h: HabitRec) => (h.dueDate ? `${h.dueDate}${h.dueTime ?? '99'}` : '9999');
  const sorted = [...tasks].sort((a, b) => order(a).localeCompare(order(b)));
  const overdue = tasks.filter((h) => h.dueDate && h.dueDate < ctx.today).length;
  const undated = tasks.filter((h) => !h.dueDate).length;
  const parts: Part[] = [
    text('chat.a.tasksIntro', { count: tasks.length }),
    { t: 'tasks', ids: sorted.slice(0, 15).map((h) => h.id) },
    { t: 'list', title: null, items: sorted.slice(0, 15).map((h) => tr('chat.a.taskItem', { name: h.name, when: when(h) })), plain: true },
  ];
  if (tasks.length > 15) parts.push(caption('chat.a.more', { count: tasks.length - 15 }));
  if (overdue || undated) parts.push(caption('chat.a.tasksHint', { overdue, undated }));
  return {
    parts,
    refs: tasks.length === 1 ? { taskId: tasks[0]!.id } : {},
    choices: overdue || undated ? [say('c-week', tr('chat.c.planWeek'), t('chat.say.planWeek'))] : [],
  };
}

/** Agenda de uno o varios días (máximo 7): hábitos programados, tareas y fechas del cerebro. */
export function agendaAnswer(env: QueryEnv, from: LocalDate, to: LocalDate): Answer {
  const { data, ctx, locale } = env;
  const t = makeT(locale);
  const idx = indexData(data);
  const settings = toEngineSettings(data.settings!);
  const last = diffDays(from, to) > 6 ? addDays(from, 6) : to;
  const parts: Part[] = [];
  let total = 0;
  for (let day = from; day <= last; day = addDays(day, 1)) {
    const a = buildAgenda({ habits: data.habits, reminders: data.reminders, notes: data.notes, vacations: data.vacations, idx, settings, day, today: ctx.today });
    const entries = SLOTS.flatMap((s) => a.slots[s]);
    total += entries.length;
    if (!entries.length && from !== last) continue;
    const items = entries.slice(0, 12).map((e) =>
      tr('chat.a.agendaItem', { time: e.time ?? t('agenda.slots.anytime'), name: e.title, kind: t(`chat.kind.${e.kind}`), state: e.done ? t('chat.a.doneMark') : e.overdue ? t('chat.a.overdueMark') : '' }),
    );
    parts.push({ t: 'list', title: tr('chat.a.agendaDay', { date: formatDay(day, locale), pending: a.pending, done: a.done }), items, plain: true });
  }
  const period = from === last ? formatDay(from, locale) : t('chat.a.range', { from: formatDay(from, locale, { day: 'numeric', month: 'short' }), to: formatDay(last, locale, { day: 'numeric', month: 'short' }) });
  if (total) parts.unshift({ t: 'agenda', from, to: last });
  if (!total) parts.unshift(text('chat.a.agendaEmpty', { period }));
  else if (from !== last) parts.unshift(text('chat.a.agendaWeek', { period, count: total }));
  parts.push(caption('chat.a.agendaSource'));
  const choices: Choice[] = from === ctx.today && last === ctx.today ? [{ id: 'c-open', label: tr('chat.c.openAgenda'), action: { type: 'navigate', to: '/', agenda: true } }] : [];
  return { parts, choices, refs: { date: from } };
}

export function focusAnswer(env: QueryEnv): Answer {
  const { data, ctx, locale } = env;
  const t = makeT(locale);
  const settings = toEngineSettings(data.settings!);
  const s = focusStats(data.focus, ctx.today, settings);
  const week = weekPeriods(settings, ctx.today, ctx.today)[0]!;
  const parts: Part[] = [];
  if (!s.sessionsWeek && !s.todayMinutes) parts.push(text('chat.a.focusNone'));
  else {
    parts.push(text('chat.a.focus', { today: s.todayMinutes, week: s.weekMinutes, count: s.sessionsWeek }));
    const name = (id: string | null) => (id ? (data.habits.find((h) => h.id === id)?.name ?? t('focus.noLink')) : t('focus.noLink'));
    if (s.byHabit.length) parts.push({ t: 'list', title: null, items: s.byHabit.slice(0, 5).map((b) => tr('chat.a.focusItem', { name: name(b.habitId), minutes: b.minutes })) });
  }
  parts.push(caption('chat.a.periodWeek', { from: formatDay(week.start, locale, { day: 'numeric', month: 'short' }), to: formatDay(week.end, locale, { day: 'numeric', month: 'short' }) }));
  return { parts, choices: [say('c-focus', tr('chat.c.startFocus'), t('chat.say.startFocus'))] };
}

export function notesAnswer(env: QueryEnv, query: string): Answer {
  const { data } = env;
  const found = searchNotes(data.notes, { query, kind: 'all' }, data.listItems).slice(0, 5);
  const parts: Part[] = [];
  if (!found.length) parts.push(text(query ? 'chat.a.notesNone' : 'chat.a.notesEmpty', { query }));
  else {
    parts.push(text(query ? 'chat.a.notesFound' : 'chat.a.notesRecent', { count: found.length, query }));
    parts.push({ t: 'notes', ids: found.map((n) => n.id) });
  }
  parts.push(caption('chat.a.notesSource'));
  return { parts, choices: [{ id: 'c-brain', label: tr('chat.c.openBrain'), action: { type: 'navigate', to: '/brain' } }] };
}

/** Solo se llama cuando el usuario pide explícitamente el diario. */
export function journalAnswer(env: QueryEnv, date: LocalDate | null): Answer {
  const { data, locale } = env;
  const live = data.journal.filter((j) => !j.deletedAt && j.text.trim());
  const pick = date ? live.filter((j) => j.date === date) : live.sort((a, b) => b.date.localeCompare(a.date)).slice(0, 1);
  const parts: Part[] = [];
  if (!pick.length) parts.push(text(date ? 'chat.a.journalNoneOn' : 'chat.a.journalNone', { date: date ? formatDay(date, locale) : '' }));
  else {
    parts.push(text('chat.a.journalOn', { date: formatDay(pick[0]!.date, locale) }));
    parts.push({ t: 'journal', ids: pick.slice(0, 3).map((j) => j.id) });
  }
  parts.push(caption('chat.a.journalPrivacy'));
  return { parts };
}

/** Hora local (0–23) en la zona del usuario. */
function hourIn(now: number, tz: string): number {
  return Number(new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', hourCycle: 'h23' }).format(now));
}

/** Datos del día para los resúmenes (chat y notificación): progreso, lo próximo con hora y avisos. */
export function daySummary(env: QueryEnv & { now: number }): { done: number; total: number; next: { time: string; title: string }[]; overdue: number; conflicts: number; empty: boolean } {
  const { data, ctx } = env;
  const settings = toEngineSettings(data.settings!);
  const a = buildAgenda({ habits: data.habits, reminders: data.reminders, notes: data.notes, vacations: data.vacations, idx: indexData(data), settings, day: ctx.today, today: ctx.today });
  const entries = SLOTS.flatMap((s) => a.slots[s]);
  const now = new Intl.DateTimeFormat('en-GB', { timeZone: ctx.tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(env.now);
  return {
    done: a.done,
    total: a.done + a.pending,
    next: entries.filter((e) => !e.done && e.time && e.time >= now).slice(0, 3).map((e) => ({ time: e.time!, title: e.title })),
    overdue: openTasks(data).filter((x) => x.dueDate && x.dueDate < ctx.today).length,
    conflicts: a.conflicts.length,
    empty: entries.length === 0,
  };
}

/**
 * Resumen del día al abrir el chat: progreso real de hoy, lo próximo con hora y las tareas
 * vencidas. Sin datos no inventa nada: lo dice e invita a empezar.
 */
export function dailyBrief(env: QueryEnv & { now: number }): Answer {
  const { ctx, locale } = env;
  const t = makeT(locale);
  const h = hourIn(env.now, ctx.tz);
  const hello = h < 13 ? 'chat.brief.morning' : h < 20 ? 'chat.brief.afternoon' : 'chat.brief.evening';
  const d = daySummary(env);
  const parts: Part[] = [];
  if (d.empty) {
    parts.push(text(hello), text('chat.brief.empty'));
    return { parts, choices: [say('c-goal', tr('chat.c.createHabit'), t('chat.examples.goal')), say('c-week', tr('chat.c.planWeek'), t('chat.say.planWeek'))] };
  }
  parts.push(text(hello), text('chat.brief.progress', { done: d.done, total: d.total }));
  parts.push({ t: 'agenda', from: ctx.today, to: ctx.today });
  if (d.next.length) parts.push({ t: 'list', title: tr('chat.brief.next'), items: d.next.map((e) => tr('chat.brief.nextItem', { time: e.time, name: e.title })), plain: true });
  if (d.overdue) parts.push(caption('chat.brief.overdue', { count: d.overdue }));
  if (d.conflicts) parts.push(caption('chat.brief.conflicts', { count: d.conflicts }));
  parts.push(caption('chat.brief.source'));
  return { parts, choices: [say('c-week', tr('chat.c.planWeek'), t('chat.say.planWeek')), say('c-neglected', tr('chat.c.neglected'), t('chat.say.neglected'))] };
}

/** Texto corto para la notificación del resumen de la mañana (mismos datos que el del chat). */
export function briefNotification(env: QueryEnv & { now: number }): { title: string; body: string } {
  const t = makeT(env.locale);
  const d = daySummary(env);
  if (d.empty) return { title: t('chat.brief.pushTitle'), body: t('chat.brief.pushEmpty') };
  const lines = [t('chat.brief.pushProgress', { count: d.total })];
  if (d.next[0]) lines.push(t('chat.brief.pushNext', { time: d.next[0].time, name: d.next[0].title }));
  if (d.overdue) lines.push(t('chat.brief.overdue', { count: d.overdue }));
  return { title: t('chat.brief.pushTitle'), body: lines.join(' ') };
}
